import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentIcon,
  EnvelopeIcon,
  PhotoIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Tooltip,
} from "@heroui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getEventShareHistory } from "../../services/api";
import {
  EventShareHistoryResponse,
  FileMetadataDto,
} from "../../types/types";

interface EventShareHistoryProps {
  eventId: string;
  eventName: string;
  username: string;
}

interface ApiErrorShape {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== "object") return fallback;

  const apiError = error as ApiErrorShape;
  return apiError.response?.data?.message || apiError.message || fallback;
};

const formatDateTime = (value: string, locale?: string): string => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

const formatFileSize = (bytes?: number): string => {
  const size = bytes || 0;

  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileTypeLabel = (fileType?: string): string => {
  if (!fileType) return "FILE";

  const [, subtype] = fileType.split("/");
  return (subtype || fileType).toUpperCase();
};

const EventShareHistory: React.FC<EventShareHistoryProps> = ({
  eventId,
  eventName,
  username,
}) => {
  const { t, i18n } = useTranslation("events");

  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState<EventShareHistoryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadingAttachment, setDownloadingAttachment] = useState<
    string | null
  >(null);

  const loadShareHistory = useCallback(async () => {
    if (!eventId) return;

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await getEventShareHistory(username, eventId);
      setHistory(response);
    } catch (error) {
      setHistory([]);
      setErrorMessage(
        getErrorMessage(
          error,
          t("failed_to_load_share_history", "Failed to load share history"),
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [eventId, username, t]);

  const handleDownload = async (
    file: FileMetadataDto,
    attachmentKey: string,
  ) => {
    if (downloadingAttachment) return;

    setDownloadingAttachment(attachmentKey);

    try {
      const response = await fetch(file.fileUrl);

      if (!response.ok) {
        throw new Error("Unable to download file");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(objectUrl);
    } catch {
      const link = document.createElement("a");

      link.href = file.fileUrl;
      link.download = file.fileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setDownloadingAttachment(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadShareHistory();
  }, [isOpen, loadShareHistory]);

  return (
    <>
      <Tooltip
        content={t("view_share_history", "View share history")}
        placement="bottom"
      >
        <Button
          isIconOnly
          size="sm"
          variant="light"
          onPress={() => setIsOpen(true)}
          aria-label={t("view_share_history", "View share history")}
          className="h-9 w-9 min-w-9 shrink-0 rounded-lg text-gray-700 hover:bg-gray-100"
        >
          <ClockIcon className="h-5 w-5" />
        </Button>
      </Tooltip>

      <Modal
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        size="4xl"
        backdrop="opaque"
        scrollBehavior="inside"
        classNames={{
          backdrop: "bg-black/45",
          base: "w-[calc(100vw-2rem)] max-w-[920px] max-h-[88dvh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl",
          header: "border-b border-gray-100 px-5 py-3 sm:px-6",
          body: "bg-gray-50/70 px-4 pb-4 pt-2 sm:px-6 sm:pb-5 sm:pt-3",
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                <div className="flex w-full min-w-0 items-center gap-3 pr-8">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                      <ClockIcon className="h-5 w-5" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-base font-bold text-gray-900">
                          {t("share_history", "Share History")}
                        </h3>

                        {!isLoading && !errorMessage && history.length > 0 && (
                          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-50 px-1.5 text-[10px] font-bold text-blue-600">
                            {history.length}
                          </span>
                        )}
                      </div>

                      <p className="mt-0.5 truncate text-[11px] font-normal text-gray-400">
                        {eventName}
                      </p>
                    </div>
                  </div>

                </div>
              </ModalHeader>

              <ModalBody>
                {isLoading ? (
                  <div className="flex min-h-[360px] flex-col items-center justify-center gap-3">
                    <Spinner size="sm" color="primary" />
                    <p className="text-xs font-medium text-gray-400">
                      {t("share_history_loading", "Loading share history...")}
                    </p>
                  </div>
                ) : errorMessage ? (
                  <div className="flex min-h-[360px] flex-col items-center justify-center px-4 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
                      <ClockIcon className="h-6 w-6" />
                    </div>

                    <p className="mt-3 max-w-md text-sm font-semibold text-gray-800">
                      {errorMessage}
                    </p>

                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      className="mt-4 font-semibold"
                      startContent={<ArrowPathIcon className="h-4 w-4" />}
                      onPress={loadShareHistory}
                    >
                      {t("retry", "Retry")}
                    </Button>
                  </div>
                ) : history.length === 0 ? (
                  <div className="flex min-h-[360px] flex-col items-center justify-center px-4 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-400 shadow-sm">
                      <ClockIcon className="h-6 w-6" />
                    </div>

                    <p className="mt-4 text-sm font-semibold text-gray-800">
                      {t("share_history_empty", "No share history yet")}
                    </p>

                    <p className="mt-1 max-w-sm text-xs leading-5 text-gray-400">
                      {t(
                        "share_history_empty_help",
                        "Shares for this event will appear here.",
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => {
                      const sharedByValue =
                        item.sharedByName ||
                        item.sharedByUsername ||
                        item.sharedById ||
                        "--";

                      let sharedBy = sharedByValue;
                      if (item.sharedByName) {
                        sharedBy = sharedByValue
                          .trim()
                          .toLowerCase()
                          .replace(/\b\w/g, (letter) => letter.toUpperCase());
                      }

                      const avatarLetter =
                        (item.sharedByName || item.sharedByUsername || "?")
                          .trim()
                          .charAt(0)
                          .toUpperCase() || "?";

                      const recipients = item.recipients || [];
                      const attachments = item.attachments || [];
                      const hasAttachments = attachments.length > 0;

                      return (
                        <section
                          key={item.id}
                          className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                        >
                          <div className="flex flex-col gap-2 border-b border-gray-100 bg-gray-50/60 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-gray-600 shadow-sm ring-1 ring-gray-200">
                                {avatarLetter}
                              </div>

                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-gray-900">
                                  {sharedBy}
                                </p>

                                {item.sharedByUsername && (
                                  <p className="mt-0.5 truncate text-[11px] text-gray-400">
                                    {item.sharedByUsername}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex shrink-0 items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                              <ClockIcon className="h-3.5 w-3.5" />
                              <span>
                                {formatDateTime(item.createdAt, i18n.language)}
                              </span>
                            </div>
                          </div>

                          <div
                            className={`grid px-4 py-3 ${
                              hasAttachments
                                ? "gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:gap-0"
                                : "grid-cols-1"
                            }`}
                          >
                            <div
                              className={`min-w-0 space-y-3 ${
                                hasAttachments ? "lg:pr-5" : ""
                              }`}
                            >
                              <div>
                                <div className="flex items-center gap-2">
                                  <UserIcon className="h-4 w-4 text-gray-400" />
                                  <p className="text-xs font-bold text-gray-700">
                                    {t("recipients", "Recipients")}
                                  </p>

                                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-[10px] font-bold text-gray-500">
                                    {recipients.length}
                                  </span>
                                </div>

                                <div className="mt-2 flex flex-wrap gap-2">
                                  {recipients.map((recipient, index) => {
                                    const isEmail = recipient.includes("@");

                                    return (
                                      <span
                                        key={`${recipient}-${index}`}
                                        className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[11px] font-medium text-gray-600"
                                      >
                                        {isEmail ? (
                                          <EnvelopeIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                        ) : (
                                          <UserIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                        )}

                                        <span className="truncate">
                                          {recipient}
                                        </span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>

                              {item.message && (
                                <div>
                                  <p className="text-xs font-bold text-gray-700">
                                    {t("message", "Message")}
                                  </p>

                                  <div className="mt-2 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3">
                                    <p className="whitespace-pre-wrap break-words text-xs leading-5 text-gray-700">
                                      {item.message}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {hasAttachments && (
                              <div className="min-w-0 lg:border-l lg:border-gray-200 lg:pl-5">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <DocumentIcon className="h-4 w-4 text-gray-400" />

                                    <p className="text-xs font-bold text-gray-700">
                                      {t("attachments", "Attachments")}
                                    </p>
                                  </div>

                                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-[10px] font-bold text-gray-500">
                                    {attachments.length}
                                  </span>
                                </div>

                                <div className="mt-2 space-y-2">
                                  {attachments.map((file, index) => {
                                    const attachmentKey = `${item.id}-${
                                      file.id ?? index
                                    }`;
                                    const isImage =
                                      file.fileType?.startsWith("image/");
                                    const isDownloading =
                                      downloadingAttachment === attachmentKey;

                                    return (
                                      <div
                                        key={attachmentKey}
                                        className="flex min-w-0 items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 transition-colors hover:bg-gray-100/70"
                                      >
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-gray-500 shadow-sm ring-1 ring-gray-100">
                                          {isImage ? (
                                            <PhotoIcon className="h-4 w-4" />
                                          ) : (
                                            <DocumentIcon className="h-4 w-4" />
                                          )}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                          <p
                                            className="line-clamp-2 break-all text-[11px] font-bold leading-4 text-gray-700"
                                            title={file.fileName}
                                          >
                                            {file.fileName}
                                          </p>

                                          <p className="mt-0.5 truncate text-[10px] font-medium text-gray-500">
                                            {[
                                              getFileTypeLabel(file.fileType),
                                              formatFileSize(file.fileSize),
                                            ]
                                              .filter(Boolean)
                                              .join(" • ")}
                                          </p>
                                        </div>

                                        <Tooltip
                                          content={t(
                                            "download",
                                            "Download",
                                          )}
                                          placement="left"
                                        >
                                          <Button
                                            isIconOnly
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            isLoading={isDownloading}
                                            isDisabled={
                                              Boolean(
                                                downloadingAttachment,
                                              ) && !isDownloading
                                            }
                                            onPress={() =>
                                              handleDownload(file, attachmentKey)
                                            }
                                            aria-label={t(
                                              "download",
                                              "Download",
                                            )}
                                            className="h-8 w-8 min-w-8 shrink-0 rounded-lg"
                                          >
                                            {!isDownloading && (
                                              <ArrowDownTrayIcon className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </Tooltip>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default EventShareHistory;